import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Paper,
  Slider,
  TextField,
  Typography,
} from '@mui/material';
import Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import { Save } from '@mui/icons-material';
import { NumericTextField } from '../../../components/forms/NumericTextField';
import type { RockUCSGeneral, RockUCSReadingsPayload } from '../../../types/rockUcs';
import { getRockUcsReadings } from '../../../services/rockUcsService';
import {
  assembleReportComments,
  buildFailureDescriptionPreview,
  buildPoissonStatements,
  classifyPoisson,
} from './computations';
import { buildPlotCutoffModel, cutoffForSave, effectivePlotEnd } from '../../../utils/plotCutoff';
import { fmt, inputSx, paperSx, SectionHeader, unit } from './sectionStyles';

interface Props {
  workOrderDetailKey: string;
  general: RockUCSGeneral;
  onChange: (field: keyof RockUCSGeneral, value: number | string | null) => void;
  onSave: () => void;
  onNotify: (message: string, severity: 'success' | 'error') => void;
  readOnly?: boolean;
}

const SERIES_COLORS = { axial: '#1A1A1A', radial: '#3B5B7E', volumetric: '#C62828' };

// BUG-2026-0151 — stress–strain data is NOT sorted by x: the axial branch runs
// right-to-left and every curve retraces its x after the peak. Highcharts LINE
// series require ascending x (error #15); above ~300 points their sorted-only
// crop machinery blanks or mangles the series and goes stale while the
// post-peak slider is dragged. SCATTER series skip sorting/cropping entirely
// (sorted: false) and, given a lineWidth, connect the points in true data
// order — Highcharts' documented way to draw unsorted line data. The tooltip
// formats restore the line-series hover look.
const unsortedLineSeries = {
  type: 'scatter',
  lineWidth: 2,
  findNearestPointBy: 'xy',
  tooltip: {
    headerFormat: '<span style="font-size: 0.8em">{point.key}</span><br/>',
    pointFormat: '<span style="color:{point.color}">●</span> {series.name}: <b>{point.y}</b><br/>',
  },
} as const;

/**
 * Analysis & Plots (REQ-08/REQ-09): the primary 17×14 stress–strain plot
 * (Y = axial stress, 17 divisions; X = average microstrain, 14 divisions,
 * reversed) with manual axis overrides; the internal-only Poisson reference
 * chart with the alternative-%-of-UCS picker; and the assembled report
 * comments preview with the remarks box.
 */
export const RockUCSAnalysisTab = ({ workOrderDetailKey, general, onChange, onSave, onNotify, readOnly }: Props) => {
  const [payload, setPayload] = useState<RockUCSReadingsPayload | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    getRockUcsReadings(workOrderDetailKey)
      .then((p) => { if (!cancelled) setPayload(p); })
      .catch(() => { if (!cancelled) onNotify('Failed to load readings', 'error'); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [workOrderDetailKey, onNotify]);

  // BUG-2026-0137 §8 — the plot follows the DRAFT slider cutoff live
  // (null = all data); Save persists it and the report uses the saved value.
  // Rework: the post-failure machine tail (payload.plotEndIndex) is never
  // plotted — it dives the curves to zero and doubles the lines back.
  const chartRows = useMemo(() => {
    if (!payload) return [];
    const end = effectivePlotEnd(general.plotCutoffIndex, payload.readings.length, payload.plotEndIndex);
    return payload.readings.slice(0, end + 1);
  }, [payload, general.plotCutoffIndex]);

  const cutoffModel = useMemo(
    () => (payload ? buildPlotCutoffModel(payload.readings.length, payload.maxRowIndex, general.plotCutoffIndex, payload.plotEndIndex) : null),
    [payload, general.plotCutoffIndex],
  );

  // ── Primary stress–strain plot (matches report page 2 geometry) ──
  const primaryOptions = useMemo((): Highcharts.Options | null => {
    if (!payload || chartRows.length === 0 || !payload.axisScale) return null;
    const scale = payload.axisScale;
    const pick = (xField: 'computedAvgAxialStrainUE' | 'computedRadialStrainUE' | 'computedVolumetricStrainUE') =>
      chartRows
        .filter((r) => r[xField] !== null && r.computedAxialStressMPa !== null)
        .map((r) => [r[xField] as number, r.computedAxialStressMPa as number]);
    return {
      chart: { height: 560, animation: false },
      title: { text: undefined },
      credits: { enabled: false },
      legend: { align: 'left', verticalAlign: 'bottom', floating: false, itemStyle: { fontSize: '0.7rem' } },
      xAxis: {
        title: { text: 'Average Microstrain (µε)' },
        reversed: true, // +7000 left → −7000 right, like the printed report
        min: scale.xMinUE,
        max: scale.xMaxUE,
        tickInterval: scale.xIntUE,
        gridLineWidth: 1,
      },
      yAxis: {
        title: { text: 'Axial Stress σ (MPa)' },
        min: scale.yMinMPa,
        max: scale.yMaxMPa,
        tickInterval: scale.yIntMPa,
        gridLineWidth: 1,
      },
      tooltip: { valueDecimals: 1 },
      plotOptions: { series: { animation: false, marker: { enabled: false } } },
      series: [
        { ...unsortedLineSeries, name: 'Axial Strain', data: pick('computedAvgAxialStrainUE'), color: SERIES_COLORS.axial },
        { ...unsortedLineSeries, name: 'Radial Strain', data: pick('computedRadialStrainUE'), color: SERIES_COLORS.radial, dashStyle: 'Dash' },
        { ...unsortedLineSeries, name: 'Volumetric Strain', data: pick('computedVolumetricStrainUE'), color: SERIES_COLORS.volumetric, dashStyle: 'ShortDash' },
      ],
    };
  }, [payload, chartRows]);

  // ── Poisson reference chart (internal only, free scale) ──
  // Loading branch ONLY (rows up to the peak): ν is read at a % of UCS on the
  // way up, and post-peak stress retraces its X range, drawing the line back
  // over itself as vertical artifacts (BUG-2026-0137 rework).
  const poissonRows = useMemo(() => {
    if (!payload) return [];
    const end = payload.maxRowIndex !== null
      ? Math.min(payload.maxRowIndex, chartRows.length - 1)
      : chartRows.length - 1;
    return chartRows.slice(0, end + 1);
  }, [payload, chartRows]);

  const poissonOptions = useMemo((): Highcharts.Options | null => {
    if (poissonRows.length === 0) return null;
    const secant = poissonRows
      .filter((r) => r.computedAxialStressMPa !== null && r.computedPoissonSecant !== null)
      .map((r) => [r.computedAxialStressMPa as number, r.computedPoissonSecant as number]);
    const tangent = poissonRows
      .filter((r) => r.computedAxialStressMPa !== null && r.computedPoissonTangent !== null)
      .map((r) => [r.computedAxialStressMPa as number, r.computedPoissonTangent as number]);
    return {
      chart: { height: 300, animation: false },
      title: { text: undefined },
      credits: { enabled: false },
      legend: { itemStyle: { fontSize: '0.68rem' } },
      xAxis: { title: { text: 'Axial Stress (MPa)', style: { fontSize: '0.68rem' } } },
      yAxis: {
        title: { text: "Poisson's Ratio ν", style: { fontSize: '0.68rem' } },
        // Fixed 0–1 scale like the KeyLAB workbook chart — near failure the
        // tangent ν blows up (radial modulus → 0); auto-scaling to that spike
        // squashed the useful 0–0.5 region into a flat line.
        min: 0,
        max: 1,
        tickInterval: 0.1,
        plotBands: [
          { from: 0.1, to: 0.4, color: 'rgba(74, 103, 65, 0.07)', label: { text: 'typical range', style: { fontSize: '0.6rem', color: '#888' } } },
        ],
        plotLines: [
          { value: 0, color: '#C62828', width: 1, dashStyle: 'Dash' },
          { value: 0.5, color: '#C62828', width: 1, dashStyle: 'Dash' },
        ],
      },
      tooltip: { valueDecimals: 3 },
      plotOptions: { series: { animation: false, marker: { enabled: false } } },
      series: [
        // Same unsorted-x hazard: the x here is measured stress, which jitters
        // non-monotonically on real load-cell data (BUG-2026-0151).
        { ...unsortedLineSeries, name: 'Secant ν', data: secant, color: SERIES_COLORS.radial },
        { ...unsortedLineSeries, name: 'Tangent ν', data: tangent, color: '#B26A00', dashStyle: 'Dash' },
      ],
    };
  }, [poissonRows]);

  // ── Reported values + validity ──
  const reported = useMemo(() => {
    const pick = (override: number | null, computed: number | null | undefined) => override ?? computed ?? null;
    return {
      poissonSecant: pick(general.overridePoissonSecant, general.computedPoissonSecant),
      poissonTangent: pick(general.overridePoissonTangent, general.computedPoissonTangent),
      eSecant: pick(general.overrideESecantGPa, general.computedESecantGPa),
      eTangent: pick(general.overrideETangentGPa, general.computedETangentGPa),
      ucs: pick(general.overrideUCSMPa, general.computedUCSMPa),
    };
  }, [general]);

  const commentsPreview = useMemo(() => {
    const failReasons: string[] = []; // shape statement is recomputed server-side; preview uses saved states
    const checks: [boolean | null | undefined, string][] = [
      [general.computedFlatnessPass, 'end flatness exceeds 0.025 mm'],
      [general.computedPerpendicularityPass, 'end perpendicularity exceeds 0.25°'],
      [general.computedParallelismPass, 'end parallelism exceeds 0.13°'],
      [general.computedSideStraightnessPass, `side straightness = ${general.sideStraightnessMm ?? '—'} mm`],
      [general.computedLOverDPass, `L/D ratio = ${fmt(general.computedLOverD ?? null, 2)}`],
    ];
    for (const [pass, reason] of checks) if (pass === false) failReasons.push(reason);
    if (general.conformityFailReason) failReasons.push(general.conformityFailReason);
    const shapeStatement = failReasons.length > 0
      ? `Specimen does not meet ASTM D4543 shape tolerance (${failReasons.join(', ')}).`
      : null;
    return assembleReportComments({
      shapeToleranceStatement: shapeStatement,
      failureDescription: general.failureDescriptionOverride
        ?? buildFailureDescriptionPreview({
          mode: general.failureMode,
          primaryPlaneType: general.primaryPlaneType,
          primaryPlaneAngleDeg: general.primaryPlaneAngleDeg,
          secondaryPlaneType: general.secondaryPlaneType,
          secondaryPlaneAngleDeg: general.secondaryPlaneAngleDeg,
        }),
      poissonStatements: buildPoissonStatements({
        poissonSecant: reported.poissonSecant,
        poissonTangent: reported.poissonTangent,
        altPercentOfUCS: general.altPercentOfUCS,
        altPoissonSecant: general.computedAltPoissonSecant,
        altPoissonTangent: general.computedAltPoissonTangent,
      }),
      remarks: general.remarksText,
    });
  }, [general, reported]);

  const validityChip = useCallback((value: number | null, label: string) => {
    const validity = classifyPoisson(value);
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
        <Typography variant="body2" sx={{ fontSize: '0.74rem' }}>{label}</Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
          <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '0.76rem' }}>
            {validity === 'invalid' ? 'invalid' : fmt(value, 3)}
          </Typography>
          {validity && (
            <Chip
              size="small"
              label={validity}
              color={validity === 'valid' ? 'success' : validity === 'atypical' ? 'warning' : 'error'}
              sx={{ height: 18, fontSize: '0.62rem' }}
            />
          )}
        </Box>
      </Box>
    );
  }, []);

  if (loading) {
    return <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress /></Box>;
  }

  if (!payload || payload.readings.length === 0) {
    return (
      <Paper variant="outlined" sx={{ p: 4, textAlign: 'center' }}>
        <Typography color="text.secondary">No machine data yet — upload the GCTS CSV on the Result Entry tab.</Typography>
      </Paper>
    );
  }

  return (
    <Box sx={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1.5fr) minmax(0, 1fr)', gap: 1.5, pb: 1 }}>
      {/* ── Primary plot + axis overrides ── */}
      <Paper variant="outlined" sx={paperSx}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <SectionHeader label="Stress–Strain Plot (report chart — 17 × 14 grid, 1 cm per division)" />
          {!readOnly && (
            <Button size="small" startIcon={<Save sx={{ fontSize: 14 }} />} onClick={onSave} sx={{ textTransform: 'none', fontSize: '0.7rem' }}>
              Save
            </Button>
          )}
        </Box>
        {/* immutable: recreate the chart on every options change. The default
            chart.update() path patches points by MATCHING X VALUES
            (Series.updateData); the strain columns are integer-quantized so x
            duplicates are common, and the matcher silently mispairs rows —
            after one slider round-trip the series held hybrid data from
            different cutoffs (BUG-2026-0151 follow-up). */}
        {primaryOptions && <HighchartsReact highcharts={Highcharts} options={primaryOptions} immutable />}
        {/* BUG-2026-0137 §8 — post-peak data slider: default all data; slide
            left to trim rows from the end of the test. */}
        {cutoffModel && payload && (
          <Box sx={{ display: 'flex', gap: 1.5, mt: 0.5, px: 1, alignItems: 'center' }}>
            <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.68rem', flexShrink: 0 }}>
              Post-peak data shown:
            </Typography>
            <Slider
              size="small"
              disabled={readOnly}
              min={cutoffModel.min}
              max={cutoffModel.max}
              value={cutoffModel.value}
              onChange={(_, v) => onChange('plotCutoffIndex', cutoffForSave(v as number, cutoffModel.max))}
              valueLabelDisplay="auto"
              valueLabelFormat={(v) => `row ${(v as number) + 1}`}
              sx={{ maxWidth: 320 }}
            />
            <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.68rem', flexShrink: 0 }}>
              {chartRows.length.toLocaleString()} of {(cutoffModel.max + 1).toLocaleString()} rows
              {general.plotCutoffIndex == null ? ' (all data)' : ''}
              {payload.plotEndIndex !== null && payload.plotEndIndex < payload.readings.length - 1
                ? ` · ${(payload.readings.length - 1 - payload.plotEndIndex).toLocaleString()} post-failure rows excluded`
                : ''} — Save to apply to the report.
            </Typography>
          </Box>
        )}
        {/* No slider (all post-peak rows are machine tail) — still say why the
            plot stops at the peak. */}
        {!cutoffModel && payload && payload.plotEndIndex !== null && payload.plotEndIndex < payload.readings.length - 1 && (
          <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.68rem', mt: 0.5, px: 1, display: 'block' }}>
            {(payload.readings.length - 1 - payload.plotEndIndex).toLocaleString()} post-failure rows
            (machine unload after peak) are excluded from the plot.
          </Typography>
        )}
        <Box sx={{ display: 'flex', gap: 1, mt: 1, flexWrap: 'wrap', alignItems: 'center' }}>
          <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.68rem' }}>
            Manual axis override (rare — leave blank for auto):
          </Typography>
          {([
            ['axisXMaxUE', 'X max (µε)'], ['axisXMinUE', 'X min (µε)'], ['axisXIntUE', 'X int (µε)'],
            ['axisYMaxMPa', 'Y max (MPa)'], ['axisYMinMPa', 'Y min (MPa)'], ['axisYIntMPa', 'Y int (MPa)'],
          ] as [keyof RockUCSGeneral, string][]).map(([field, label]) => (
            <NumericTextField
              key={field}
              size="small" label={label}
              value={general[field] as number | null}
              onChange={(v) => onChange(field, v)}
              InputProps={{ readOnly }}
              sx={{ ...inputSx, width: 108 }}
            />
          ))}
          <Typography variant="caption" color="text.disabled" sx={{ fontSize: '0.64rem' }}>
            Save to apply — the chart and report use the saved scale.
          </Typography>
        </Box>
      </Paper>

      {/* ── Right column: Poisson reference + summary + comments ── */}
      <Box sx={{ display: 'grid', rowGap: 1.5, alignContent: 'start' }}>
        <Paper variant="outlined" sx={paperSx}>
          <SectionHeader label="Poisson's Reference Chart (internal only)" />
          {poissonOptions && <HighchartsReact highcharts={Highcharts} options={poissonOptions} immutable />}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
            <NumericTextField
              size="small" label="Alternative % of UCS"
              value={general.altPercentOfUCS}
              onChange={(v) => onChange('altPercentOfUCS', v)}
              InputProps={{ endAdornment: unit('%'), readOnly }}
              sx={{ ...inputSx, width: 180, flexShrink: 0 }}
              helperText="Used when the 50% ν is invalid"
            />
            {general.altPercentOfUCS != null && (
              <Typography variant="caption" sx={{ fontSize: '0.7rem' }}>
                ν at {general.altPercentOfUCS}%: secant {fmt(general.computedAltPoissonSecant ?? null, 3)} ·
                tangent {fmt(general.computedAltPoissonTangent ?? null, 3)} (after save)
              </Typography>
            )}
          </Box>
        </Paper>

        <Paper variant="outlined" sx={paperSx}>
          <SectionHeader label="Reported Summary" />
          <Box sx={{ display: 'grid', rowGap: 0.5 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
              <Typography variant="body2" sx={{ fontSize: '0.74rem' }}>UCS</Typography>
              <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '0.76rem' }}>{fmt(reported.ucs, 1)} MPa</Typography>
            </Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
              <Typography variant="body2" sx={{ fontSize: '0.74rem' }}>E Secant / Tangent</Typography>
              <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '0.76rem' }}>
                {fmt(reported.eSecant, 1)} / {fmt(reported.eTangent, 1)} GPa
              </Typography>
            </Box>
            {validityChip(reported.poissonSecant, "Poisson's Ratio — Secant")}
            {validityChip(reported.poissonTangent, "Poisson's Ratio — Tangent")}
          </Box>
        </Paper>

        <Paper variant="outlined" sx={paperSx}>
          <SectionHeader label="Report Comments" />
          <Typography
            variant="body2"
            sx={{ fontSize: '0.74rem', whiteSpace: 'pre-line', backgroundColor: 'rgba(0,0,0,0.03)', p: 1, borderRadius: 1, mb: 1, minHeight: 48 }}
          >
            {commentsPreview || '— no comments yet —'}
          </Typography>
          <TextField
            size="small" fullWidth multiline minRows={2}
            label="Remarks (appended to the report comments)"
            value={general.remarksText ?? ''}
            onChange={(e) => onChange('remarksText', e.target.value || null)}
            InputProps={{ readOnly }} sx={inputSx}
          />
        </Paper>
      </Box>
    </Box>
  );
};
